import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Organization, User } from '@shared/types';
import { fetchMe, login as loginRequest, logout as logoutRequest } from './api';

export interface AuthState {
  user: User | null;
  organization: Organization | null;
  loading: boolean;
  error: string | null;
}

export interface AuthContextValue extends AuthState {
  signIn: (email: string, password: string) => Promise<User>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    organization: null,
    loading: true,
    error: null,
  });

  const refresh = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const me = await fetchMe();
      setState({
        user: me.user,
        organization: me.organization,
        loading: false,
        error: null,
      });
    } catch (err) {
      setState({
        user: null,
        organization: null,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to load session.',
      });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signIn = useCallback(async (email: string, password: string) => {
    const me = await loginRequest(email, password);
    if (!me.user) throw new Error('Login failed.');
    setState({
      user: me.user,
      organization: me.organization,
      loading: false,
      error: null,
    });
    return me.user;
  }, []);

  const signOut = useCallback(async () => {
    await logoutRequest();
    setState({ user: null, organization: null, loading: false, error: null });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, signIn, signOut, refresh }),
    [state, signIn, signOut, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
